import { useEffect, useState } from 'react';

/**
 * Whether a media query matches, as state.
 *
 * One render path rather than two: the alternative is rendering both a table
 * and a card list and letting CSS hide one, which doubles the DOM and means
 * every assertion has to say which of the two copies it meant.
 *
 * Where matchMedia does not exist - server rendering, a test environment - it
 * answers `true`, the wide layout. That is a choice, not a default: the wide
 * layout is the one with the table in it, and a check looking for a table
 * inside a scroll container should find one.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(query);
    const listen = (event: MediaQueryListEvent): void => setMatches(event.matches);
    setMatches(media.matches);
    media.addEventListener('change', listen);
    return () => media.removeEventListener('change', listen);
  }, [query]);

  return matches;
}
